import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { Settings, Save } from 'lucide-react'

interface StampFilterSettingsProps {
  tenantId: string
}

const StampFilterSettings = ({ tenantId }: StampFilterSettingsProps) => {
  const [allowStampFilter, setAllowStampFilter] = useState(true)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const { toast } = useToast()

  useEffect(() => {
    fetchSettings()
  }, [tenantId])

  const fetchSettings = async () => {
    try {
      setLoading(true)
      
      const { data, error } = await supabase
        .from('tenants')
        .select('settings')
        .eq('id', tenantId)
        .single()

      if (error) throw error

      const settings = data.settings || {}
      setAllowStampFilter((settings as any).allow_stamp_filter !== false)
    } catch (error) {
      console.error('Error fetching settings:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Einstellungen konnten nicht geladen werden.",
      })
    } finally {
      setLoading(false)
    }
  }

  const saveSettings = async () => {
    try {
      setSaving(true)
      
      const { error } = await supabase
        .from('tenants')
        .update({
          settings: {
            allow_stamp_filter: allowStampFilter
          }
        })
        .eq('id', tenantId)

      if (error) throw error

      toast({
        title: "Einstellungen gespeichert",
        description: "Die Stempel-Filter Einstellungen wurden erfolgreich aktualisiert.",
      })
    } catch (error) {
      console.error('Error saving settings:', error)
      toast({
        variant: "destructive",
        title: "Fehler",
        description: "Einstellungen konnten nicht gespeichert werden.",
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <p>Einstellungen werden geladen...</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Stempel-Filter Einstellungen
        </CardTitle>
        <CardDescription>
          Bestimmen Sie, ob Ihre Kunden zwischen verschiedenen Stempeltypen filtern können
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between space-x-2">
          <div className="space-y-1">
            <Label htmlFor="allow-filter" className="text-base font-medium">
              Stempel-Filter für Kunden aktivieren
            </Label>
            <p className="text-sm text-muted-foreground">
              Erlaubt Kunden, in ihrer Historie zwischen Besuchs- und Empfehlungsstempeln zu filtern
            </p>
          </div>
          <Switch
            id="allow-filter"
            checked={allowStampFilter}
            onCheckedChange={setAllowStampFilter}
          />
        </div>

        <div className="pt-4 border-t">
          <div className="space-y-2">
            <h4 className="font-medium">Vorschau für Kunden:</h4>
            <div className="p-3 bg-muted/50 rounded-lg text-sm">
              {allowStampFilter ? (
                <div className="space-y-1">
                  <p className="text-green-600 font-medium">✓ Kunden können filtern zwischen:</p>
                  <ul className="ml-4 space-y-1 text-muted-foreground">
                    <li>• Alle Stempel anzeigen</li>
                    <li>• Nur Besuchsstempel</li>
                    <li>• Nur Empfehlungsstempel</li>
                  </ul>
                </div>
              ) : (
                <p className="text-orange-600 font-medium">✗ Kunden sehen alle Stempel ohne Filteroption</p>
              )}
            </div>
          </div>
        </div>

        <Button onClick={saveSettings} disabled={saving} className="w-full">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Wird gespeichert...' : 'Einstellungen speichern'}
        </Button>
      </CardContent>
    </Card>
  )
}

export default StampFilterSettings